import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CartItem } from '../types';
import { createStripeCheckoutSession } from '../services/stripeService';
import { getProductImageForColor, getColorName } from '../constants';

interface CheckoutProps {
  cart: CartItem[];
  removeFromCart: (productId: string, size: string, color: string) => void;
  updateQuantity: (productId: string, size: string, color: string, quantity: number) => void;
}

const Checkout: React.FC<CheckoutProps> = ({ cart, removeFromCart, updateQuantity }) => {
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('ES');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subtotal = useMemo(() => cart.reduce((sum, i) => sum + (i.product.price * i.quantity), 0), [cart]);
  const shipping = subtotal > 65 ? 0 : 4.75;
  const taxes = subtotal * 0.21; // IVA España
  const total = subtotal + shipping;

const handleStripeRedirect = async () => {
  if (!email || !firstName || !lastName || !address || !city || !zip) {  // Añadí lastName, que faltaba en tu if
    alert("Por favor, completa todos los campos para el envío.");
    return;
  }

  setIsRedirecting(true);
  setError(null);

  try {
    // La página de éxito recupera el pedido real desde Stripe con el session_id,
    // así que ya no hace falta guardar nada en localStorage.
    const customer = { email, firstName, lastName, address, city, zip, country };

    // El backend calcula precios y envío desde su propia lista; solo enviamos ids y cantidades
    const result = await createStripeCheckoutSession(cart, email, customer);

    if (result.url) {
      window.location.href = result.url;
    } else {
      setError("No se recibió la URL de Stripe.");
      setIsRedirecting(false);
    }
  } catch (err) {
    console.error("Error handleStripeRedirect:", err);
    setError("No se pudo conectar con el servidor de pagos. Reintenta en la siguiente vuelta.");
    setIsRedirecting(false);
  }
};

  if (cart.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6">
        <span className="material-symbols-outlined text-7xl text-gray-200">shopping_cart</span>
        <h2 className="text-2xl font-bold italic">Tu carrito está vacío.</h2>
        <Link to="/shop" className="bg-black text-white px-8 py-3 rounded-lg font-bold uppercase tracking-widest hover:bg-neutral-800 transition-colors italic">
          Ver Colección
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen lg:flex-row max-w-[1600px] mx-auto relative">
      {/* OVERLAY DE REDIRECCIÓN A STRIPE */}
      {isRedirecting && (
        <div className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center p-6 text-center animate-fade-in">
          <div className="relative size-24 mb-10 flex items-center justify-center pulse-ring">
             <div className="size-40 text-primary">
		<img src="/logo_full.png" alt="Logo" className="h-full w-full object-contain" />
             </div>
          </div>
          <h2 className="text-2xl font-black uppercase italic tracking-tighter mb-2">Conectando con la plataforma de pago</h2>
          <p className="text-gray-400 text-sm font-medium italic max-w-xs">
            Estamos preparando tu pasarela de pago segura. No cierres esta ventana.
          </p>
          <div className="mt-12 flex items-center gap-4 grayscale opacity-40">
            <span className="text-[10px] font-black uppercase tracking-widest italic">Secured by Stripe</span>
            <div className="h-[1px] w-8 bg-gray-300"></div>
            <span className="text-[10px] font-black uppercase tracking-widest italic text-primary">SANAN Official Store</span>
          </div>
        </div>
      )}

      <div className="flex-1 bg-white p-6 md:p-12 lg:p-20 border-r border-gray-100">
        <header className="mb-12">
          <h1 className="text-3xl font-black tracking-tighter uppercase mb-4 italic">Finalizar Compra</h1>
          <nav className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold">
            <Link to="/shop" className="text-primary">Tienda</Link>
            <span className="text-gray-300">/</span>
            <span className="text-black">Información</span>
            <span className="text-gray-300">/</span>
            <span className="text-gray-400">Pago Seguro</span>
          </nav>
        </header>

        <section className="space-y-12">
          <div>
            <h2 className="text-xl font-black uppercase italic mb-6">Información de Contacto</h2>
            <input 
              className="w-full h-14 bg-gray-50 border-gray-100 rounded-xl px-4 outline-none font-medium focus:ring-2 focus:ring-primary/10 transition-all" 
              placeholder="Correo electrónico" 
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <p className="text-[9px] text-gray-400 uppercase font-black tracking-widest mt-2 ml-1 italic">Tus datos están protegidos por encriptación SSL.</p>
          </div>

<div>
  <h2 className="text-xl font-black uppercase italic mb-6">Dirección de Envío</h2>
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {/* Selector de país – ocupa las dos columnas */}
    <select
      className="w-full h-14 bg-gray-50 border-gray-100 rounded-xl px-4 md:col-span-2 outline-none font-medium focus:ring-2 focus:ring-primary/10 transition-all appearance-none"
      value={country}
      onChange={(e) => setCountry(e.target.value)}
      required
    >
      <option value="ES">España</option>
      <option value="PT">Portugal</option>
      <option value="FR">Francia</option>
      <option value="IT">Italia</option>
      <option value="DE">Alemania</option>
      <option value="AD">Andorra</option>
      <option value="GB">Reino Unido</option>
      <option value="BE">Bélgica</option>
      <option value="NL">Países Bajos</option>
      <option value="CH">Suiza</option>
      {/* Puedes añadir más aquí manualmente... */}

      {/* Opcional: línea separadora si quieres destacar España arriba */}
      <option value="" disabled>───────────────</option>

      {/* Resto de países – copia-pega esta lista larga */}
      <option value="AF">Afganistán</option>
      <option value="AL">Albania</option>
      <option value="DZ">Argelia</option>
      <option value="AS">Samoa Americana</option>
      <option value="AO">Angola</option>
      <option value="AI">Anguila</option>
      <option value="AQ">Antártida</option>
      <option value="AG">Antigua y Barbuda</option>
      <option value="AR">Argentina</option>
      <option value="AM">Armenia</option>
      <option value="AW">Aruba</option>
      <option value="AU">Australia</option>
      <option value="AT">Austria</option>
      <option value="AZ">Azerbaiyán</option>
      <option value="BS">Bahamas</option>
      <option value="BH">Baréin</option>
      <option value="BD">Bangladés</option>
      <option value="BB">Barbados</option>
      <option value="BY">Bielorrusia</option>
      <option value="BZ">Belice</option>
      <option value="BJ">Benín</option>
      <option value="BM">Bermudas</option>
      <option value="BT">Bután</option>
      <option value="BO">Bolivia</option>
      <option value="BA">Bosnia y Herzegovina</option>
      <option value="BW">Botsuana</option>
      <option value="BR">Brasil</option>
      <option value="BN">Brunéi</option>
      <option value="BG">Bulgaria</option>
      <option value="BF">Burkina Faso</option>
      <option value="BI">Burundi</option>
      <option value="CV">Cabo Verde</option>
      <option value="KH">Camboya</option>
      <option value="CM">Camerún</option>
      <option value="CA">Canadá</option>
      <option value="CF">República Centroafricana</option>
      <option value="TD">Chad</option>
      <option value="CL">Chile</option>
      <option value="CN">China</option>
      <option value="CO">Colombia</option>
      <option value="KM">Comoras</option>
      <option value="CG">Congo</option>
      <option value="CD">República Democrática del Congo</option>
      <option value="CR">Costa Rica</option>
      <option value="CI">Costa de Marfil</option>
      <option value="HR">Croacia</option>
      <option value="CU">Cuba</option>
      <option value="CY">Chipre</option>
      <option value="CZ">República Checa</option>
      <option value="DK">Dinamarca</option>
      <option value="DJ">Yibuti</option>
      <option value="DM">Dominica</option>
      <option value="DO">República Dominicana</option>
      <option value="EC">Ecuador</option>
      <option value="EG">Egipto</option>
      <option value="SV">El Salvador</option>
      <option value="GQ">Guinea Ecuatorial</option>
      <option value="ER">Eritrea</option>
      <option value="EE">Estonia</option>
      <option value="SZ">Esuatini</option>
      <option value="ET">Etiopía</option>
      <option value="FJ">Fiyi</option>
      <option value="FI">Finlandia</option>
      <option value="GA">Gabón</option>
      <option value="GM">Gambia</option>
      <option value="GE">Georgia</option>
      <option value="GH">Ghana</option>
      <option value="GR">Grecia</option>
      <option value="GD">Granada</option>
      <option value="GT">Guatemala</option>
      <option value="GN">Guinea</option>
      <option value="GW">Guinea-Bisáu</option>
      <option value="GY">Guyana</option>
      <option value="HT">Haití</option>
      <option value="HN">Honduras</option>
      <option value="HU">Hungría</option>
      <option value="IS">Islandia</option>
      <option value="IN">India</option>
      <option value="ID">Indonesia</option>
      <option value="IR">Irán</option>
      <option value="IQ">Irak</option>
      <option value="IE">Irlanda</option>
      <option value="IL">Israel</option>
      <option value="IT">Italia</option>
      <option value="JM">Jamaica</option>
      <option value="JP">Japón</option>
      <option value="JO">Jordania</option>
      <option value="KZ">Kazajistán</option>
      <option value="KE">Kenia</option>
      <option value="KI">Kiribati</option>
      <option value="KW">Kuwait</option>
      <option value="KG">Kirguistán</option>
      <option value="LA">Laos</option>
      <option value="LV">Letonia</option>
      <option value="LB">Líbano</option>
      <option value="LS">Lesoto</option>
      <option value="LR">Liberia</option>
      <option value="LY">Libia</option>
      <option value="LI">Liechtenstein</option>
      <option value="LT">Lituania</option>
      <option value="LU">Luxemburgo</option>
      <option value="MG">Madagascar</option>
      <option value="MW">Malaui</option>
      <option value="MY">Malasia</option>
      <option value="MV">Maldivas</option>
      <option value="ML">Malí</option>
      <option value="MT">Malta</option>
      <option value="MH">Islas Marshall</option>
      <option value="MR">Mauritania</option>
      <option value="MU">Mauricio</option>
      <option value="MX">México</option>
      <option value="FM">Micronesia</option>
      <option value="MD">Moldavia</option>
      <option value="MC">Mónaco</option>
      <option value="MN">Mongolia</option>
      <option value="ME">Montenegro</option>
      <option value="MA">Marruecos</option>
      <option value="MZ">Mozambique</option>
      <option value="MM">Birmania</option>
      <option value="NA">Namibia</option>
      <option value="NR">Nauru</option>
      <option value="NP">Nepal</option>
      <option value="NL">Países Bajos</option>
      <option value="NZ">Nueva Zelanda</option>
      <option value="NI">Nicaragua</option>
      <option value="NE">Níger</option>
      <option value="NG">Nigeria</option>
      <option value="KP">Corea del Norte</option>
      <option value="MK">Macedonia del Norte</option>
      <option value="NO">Noruega</option>
      <option value="OM">Omán</option>
      <option value="PK">Pakistán</option>
      <option value="PW">Palaos</option>
      <option value="PS">Palestina</option>
      <option value="PA">Panamá</option>
      <option value="PG">Papúa Nueva Guinea</option>
      <option value="PY">Paraguay</option>
      <option value="PE">Perú</option>
      <option value="PH">Filipinas</option>
      <option value="PL">Polonia</option>
      <option value="PT">Portugal</option>
      <option value="QA">Catar</option>
      <option value="RO">Rumania</option>
      <option value="RU">Rusia</option>
      <option value="RW">Ruanda</option>
      <option value="KN">San Cristóbal y Nieves</option>
      <option value="LC">Santa Lucía</option>
      <option value="VC">San Vicente y las Granadinas</option>
      <option value="WS">Samoa</option>
      <option value="SM">San Marino</option>
      <option value="ST">Santo Tomé y Príncipe</option>
      <option value="SA">Arabia Saudita</option>
      <option value="SN">Senegal</option>
      <option value="RS">Serbia</option>
      <option value="SC">Seychelles</option>
      <option value="SL">Sierra Leona</option>
      <option value="SG">Singapur</option>
      <option value="SK">Eslovaquia</option>
      <option value="SI">Eslovenia</option>
      <option value="SB">Islas Salomón</option>
      <option value="SO">Somalia</option>
      <option value="ZA">Sudáfrica</option>
      <option value="KR">Corea del Sur</option>
      <option value="SS">Sudán del Sur</option>
      <option value="ES" selected>España</option> {/* duplicado arriba, pero lo dejamos por si acaso */}
      <option value="LK">Sri Lanka</option>
      <option value="SD">Sudán</option>
      <option value="SR">Surinam</option>
      <option value="SE">Suecia</option>
      <option value="CH">Suiza</option>
      <option value="SY">Siria</option>
      <option value="TW">Taiwán</option>
      <option value="TJ">Tayikistán</option>
      <option value="TZ">Tanzania</option>
      <option value="TH">Tailandia</option>
      <option value="TL">Timor Oriental</option>
      <option value="TG">Togo</option>
      <option value="TO">Tonga</option>
      <option value="TT">Trinidad y Tobago</option>
      <option value="TN">Túnez</option>
      <option value="TR">Turquía</option>
      <option value="TM">Turkmenistán</option>
      <option value="TV">Tuvalu</option>
      <option value="UG">Uganda</option>
      <option value="UA">Ucrania</option>
      <option value="AE">Emiratos Árabes Unidos</option>
      <option value="GB">Reino Unido</option>
      <option value="US">Estados Unidos</option>
      <option value="UY">Uruguay</option>
      <option value="UZ">Uzbekistán</option>
      <option value="VU">Vanuatu</option>
      <option value="VE">Venezuela</option>
      <option value="VN">Vietnam</option>
      <option value="YE">Yemen</option>
      <option value="ZM">Zambia</option>
      <option value="ZW">Zimbabue</option>
    </select>

    {/* Resto de campos – igual que antes */}
	      <input 
                className="w-full h-14 bg-gray-50 border-gray-100 rounded-xl px-4" 
                placeholder="Nombre" 
                value={firstName} 
                onChange={(e) => setFirstName(e.target.value)} 
              />
              <input 
                className="w-full h-14 bg-gray-50 border-gray-100 rounded-xl px-4" 
                placeholder="Apellidos" 
                value={lastName} 
                onChange={(e) => setLastName(e.target.value)} 
              />
              <input 
                className="w-full h-14 bg-gray-50 border-gray-100 rounded-xl px-4 md:col-span-2" 
                placeholder="Dirección completa" 
                value={address} 
                onChange={(e) => setAddress(e.target.value)} 
              />
              <input 
                className="w-full h-14 bg-gray-50 border-gray-100 rounded-xl px-4" 
                placeholder="Ciudad" 
                value={city} 
                onChange={(e) => setCity(e.target.value)} 
              />
              <input 
                className="w-full h-14 bg-gray-50 border-gray-100 rounded-xl px-4" 
                placeholder="Código Postal" 
                value={zip} 
                onChange={(e) => setZip(e.target.value)} 
              />
            </div>
          </div>

          {error && (
            <div className="p-4 bg-red-50 text-red-600 rounded-xl text-xs font-bold uppercase italic border border-red-100 animate-shake">
              {error}
            </div>
          )}

          <p className="flex items-center gap-2 text-gray-500 text-xs font-bold uppercase tracking-widest">
            <span className="material-symbols-outlined text-primary text-base">sell</span>
            ¿Tienes un código de descuento? Podrás introducirlo en el siguiente paso.
          </p>

          <div className="pt-8 flex flex-col-reverse sm:flex-row items-center justify-between gap-6">
            <Link to="/shop" className="text-primary font-bold flex items-center gap-2 uppercase text-xs tracking-widest">
              <span className="material-symbols-outlined text-sm">arrow_back</span>
              Seguir Comprando
            </Link>
            <button 
              onClick={handleStripeRedirect}
              className="group w-full sm:w-auto bg-black text-white h-16 px-12 rounded-xl font-black uppercase tracking-widest hover:bg-neutral-800 transition-all shadow-xl italic flex items-center justify-center gap-3"
              disabled={isRedirecting}
            >
              {isRedirecting ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⟳</span>
                  Redirigiendo...
                </span>
              ) : (
                <>
                  Continuar al Pago
                  <span className="material-symbols-outlined text-lg transition-transform group-hover:translate-x-1">lock</span>
                </>
              )}
            </button>
          </div>
        </section>
      </div>

<aside className="w-full lg:w-[480px] bg-gray-50 p-6 md:p-12 lg:p-20">
  <div className="flex items-baseline justify-between">
    <h2 className="text-xl font-black uppercase italic mb-8">Resumen</h2>
    <span className="text-sm font-bold text-gray-600">
      {cart.reduce((sum, item) => sum + item.quantity, 0)} artículos
    </span>
  </div>
  <div className="space-y-6 mb-10">
    {cart.map((item) => (
      <div key={`${item.product.id}-${item.size}-${getColorName(item.color)}`} className="flex items-start gap-4 p-3 rounded-xl bg-white border border-gray-100 hover:bg-gray-50 transition-colors">
        {/* Imagen */}
        <div className="relative size-16 bg-white rounded-xl border border-gray-100 overflow-hidden shrink-0 flex-shrink-0">
  <img 
    src={getProductImageForColor(item.product, item.color)}   // ← ¡Aquí!
    className="w-full h-full object-cover" 
    alt={`${item.product.name} - ${getColorName(item.color)}`}
  />
</div>
        
        {/* Info del producto */}
        <div className="flex-1 min-w-0">
          <h3 className="text-xs font-black uppercase italic truncate">{item.product.name}</h3>
          <p className="text-[10px] text-gray-400 font-bold uppercase mb-2">
            {item.size} / {getColorName(item.color)} 
          </p>
          
          {/* Controles de cantidad */}
<div className="flex items-center gap-4 mb-2">
  {/* Selector de cantidad */}
<div className="flex items-center bg-white border border-gray-200 rounded-md overflow-hidden
                h-10 md:h-11 shrink-0">
  <button
    onClick={() => updateQuantity(item.product.id, item.size, item.color, item.quantity - 1)}
    disabled={item.quantity <= 1}
    className="w-10 h-10 md:w-10 md:h-10 md:min-w-[12px] shrink-0 flex items-center justify-center text-gray-600 hover:text-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
  >
    −
  </button>

  <span className="w-14 h-10 md:w-12 md:h-10 md:min-w-[20px] shrink-0 flex items-center justify-center font-medium text-base md:text-lg border-x border-gray-200">
    {item.quantity}
  </span>

  <button
    onClick={() => updateQuantity(item.product.id, item.size, item.color, item.quantity + 1)}
    disabled={item.quantity >= 20}
    className="w-10 h-10 md:w-10 md:h-10 md:min-w-[12px] shrink-0 flex items-center justify-center text-gray-600 hover:text-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
  >
    +
  </button>
</div>


  {/* Eliminar */}
  <button
    onClick={() => {
      if (confirm(`¿Eliminar ${item.product.name} (${item.size} / ${getColorName(item.color)})?`)) {
        removeFromCart(item.product.id, item.size, item.color);
      }
    }}
    className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center gap-1.5 transition-colors"
    title="Eliminar producto"
  >
    <span className="material-symbols-outlined text-base">delete</span>
    Eliminar
  </button>
</div>
        </div>
        
        {/* Precio */}
        <div className="text-right flex-shrink-0 min-w-[80px]">
          <div className="font-black text-sm italic">{(item.product.price * item.quantity).toFixed(2)}€</div>
          <div className="text-[9px] text-gray-400 font-bold uppercase">{item.product.price.toFixed(2)}€ ud.</div>
        </div>
      </div>
    ))}
  </div>
  
  <div className="border-t border-gray-200 pt-8">
    <div className="flex justify-between items-center mb-2">
      <span className="text-sm font-bold uppercase tracking-wider text-gray-600">Subtotal</span>
      <span className="font-black text-lg">{subtotal.toFixed(2)}€</span>
    </div>

    <div className="flex justify-between items-center mb-4">
      <span className="text-sm font-bold uppercase tracking-wider text-gray-600">Envío</span>
      <span className="font-medium text-sm">
        {shipping === 0 ? (
          <span className="text-green-600 font-black uppercase tracking-wider">GRATIS</span>
        ) : (
          `${shipping.toFixed(2)}€`
        )}
      </span>
    </div>

    <div className="flex justify-between items-center">
      <span className="text-xl font-black uppercase italic">Total</span>
      <span className="text-3xl font-black italic">{total.toFixed(2)}€</span>
    </div>
  </div>
</aside>
    </div>
  );
};

export default Checkout;